// CustomFormField.tsx
import { AnimationEvent, ChangeEvent, ReactNode, useState } from "react";
import {
  FieldValues,
  FieldPath,
  Control,
  useFormContext,
} from "react-hook-form";
import {
  FormItem,
  FormControl,
  FormMessage,
  FormField,
} from "@/components/ui/form";
import { Textarea } from "../ui/textarea";

type DefaultFormFieldProps<TFieldValues extends FieldValues> = {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  placeholder: string;
  className?: string;
  children?: ReactNode;
  maxLength?: number;
};

export function TextareaFormField<TFieldValues extends FieldValues>({
  name,
  control,
  placeholder,
  className,
  children,
  maxLength,
}: DefaultFormFieldProps<TFieldValues>) {
  const { setValue, getValues } = useFormContext();
  const [autofilled, setAutofilled] = useState<boolean>(false);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = getValues(name) as string;
    let newValue = e.target.value;
  
    if (maxLength !== undefined) {
      newValue = newValue.slice(0, maxLength);
    }
  
    if (autofilled && newValue !== value && !newValue.length) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setValue(name, newValue, { shouldValidate: false });
      setAutofilled(false);
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setValue(name, newValue, { shouldValidate: true });
    }
  };

  const handleAnimationStart = (e: AnimationEvent<HTMLTextAreaElement>) => {
    if (e.animationName === "onAutofill") {
      setAutofilled(true);
    }
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, formState }) => {
        const hasError = Boolean(formState.errors[name]);

        return (
          <FormItem className="relative h-full">
            <FormControl>
              <Textarea
                {...field}
                placeholder={placeholder}
                className={`w-full rounded-[10px] !h-full ${
                  hasError &&
                  "border-[red] focus:placeholder:text-[red] focus:border-[red]"
                } ${className}`}
                styleLabel={{
                  color: hasError && "red",
                }}
                onChange={handleChange}
                onAnimationStart={handleAnimationStart}
              />
            </FormControl>
            <FormMessage className="pl-[16px]" />
            {children}
          </FormItem>
        );
      }}
    />
  );
}
